import { useMemo, useRef, useState } from "react";
import { AlertDialogComponent, DataTable, Pagination } from "@/components/shared";
import { getColumnsTariff } from "./columns";
import { useIntl } from "react-intl";
import { useTranslation } from "react-i18next";
import AddTariffDialog from "./AddTariffDialog";
import { PaginatedTariffsQuery, PaginatedTariffsQueryVariables, PaginatedTariffsDocument, Tariff, TariffEdge } from "@/graphql/__generated__/graphql";
import TariffsFilter from "./TariffsFilter";
import { Button } from "@/components/ui/button";
import useUserStore from "@/store/shield";
import { useRemoveTariffMutationHook } from "@/hooks";

interface PaginationRef {
  triggerHandleFilterSubmit: (_newVariables: Partial<PaginatedTariffsQueryVariables>) => void;
}

function TariffComponent() {
  const intl = useIntl();
  const { t } = useTranslation(["translation", "common", "tables"]);
  const { removeTariffMutation } = useRemoveTariffMutationHook();

  const { isConfigAdmin } = useUserStore();

  // Pagination config
  const paginationRef = useRef<PaginationRef | null>(null);
  const paginationName = "paginatedTariffs";

  const [openAddTariffDialog, setOpenAddTariffDialog] = useState(false);
  const [isRemoveTariffOpen, setIsRemoveTariffOpen] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState<Tariff | null>(null);

  const onFilterSubmit = (variables: PaginatedTariffsQueryVariables) => {
    // When the filter is submitted in the PageComponent,
    // this will call the handleFilterSubmit inside Pagination.
    paginationRef.current?.triggerHandleFilterSubmit(variables);
  };

  const handleRemoveClick = (tariff: Tariff) => {
    setSelectedTariff(tariff);
    setIsRemoveTariffOpen(true);
  };

  const handleRemoveTariff = (tariff: Tariff) => {
    removeTariffMutation({ variables: { removeTariffId: tariff.id } }).finally(() => {
      setIsRemoveTariffOpen(false);
    });
  };

  return (
    <>
      <Pagination<TariffEdge, PaginatedTariffsQuery, PaginatedTariffsQueryVariables>
        ref={paginationRef} props={{ queryDocument: PaginatedTariffsDocument, paginationName }}>
        {(dataSlice, loading, displayAmount) => (
          <div className="flex flex-col gap-2">
            <div className="flex flex-row gap-2 justify-end">
              <TariffsFilter onFilterSubmit={onFilterSubmit} displayAmount={displayAmount as number} />
              {isConfigAdmin && <Button onClick={() => setOpenAddTariffDialog(true)}>{t("common:buttons.add")}</Button>}
            </div>
            <DataTable
              disableSorting={false}
              loading={loading}
              displayAmount={displayAmount as number}
              data={dataSlice.map(edge => edge.node) ?? []}
              columns={useMemo(() => getColumnsTariff(intl, t, handleRemoveClick, isConfigAdmin), [intl, t, handleRemoveClick, isConfigAdmin])}
              enableColumnPinning
              columnPinning={{
                left: ["province"],
                right: ["actions"]
              }}
              className="max-h-[calc(100vh-20rem)]"
            />
          </div>
        )}
      </Pagination>

      {
        openAddTariffDialog && (
          <AddTariffDialog
            open={openAddTariffDialog}
            onOpenChange={setOpenAddTariffDialog}
          />
        )
      }

      {/* Remove Tariff Alert Dialog */}
      <AlertDialogComponent
        open={isRemoveTariffOpen}
        onOpenChange={setIsRemoveTariffOpen}
        actionButtonFn={() => handleRemoveTariff(selectedTariff as Tariff)}
        title={t("components:alertdialogs.removetitle")}
        bodyText={t("components:alertdialogs.removetariff")}
      />
    </>
  );
}

export default TariffComponent;
